import type { LensTheme } from '@diligentcorp/atlas-theme-mui-lens';
import { Close } from '@mui/icons-material';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import {
  Box,
  Chip,
  Drawer,
  IconButton,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from '@mui/material';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  MitigationPlanFragment,
  useGetMitigationPlanUploadUrlMutation,
} from 'src/graphql';
import * as Types from '../../../types';
import { MitigationPlanApprovalChain } from './MitigationPlanApprovalChain';
import { MitigationPlanFileViewer } from './MitigationPlanFileViewer';

interface Props {
  open: boolean;
  plan: MitigationPlanFragment;
  onClose: () => void;
  onPlanUpdated?: () => void;
}

export const MitigationPlanDetailsDrawer = ({
  open,
  plan,
  onClose,
  onPlanUpdated,
}: Props) => {
  const currentUserId = useSelector(
    (state: Types.State) => state.globalUsers.currentUser?.id,
  );
  const needsMySignature =
    currentUserId !== undefined &&
    (plan.status === 'PENDING' || plan.status === 'OVERDUE') &&
    (plan.reviewerSignatories ?? []).some(
      (s) => !s.signedAt && !s.rejectedAt && s.userId === Number(currentUserId),
    );
  const [tab, setTab] = useState(needsMySignature ? 1 : 0);
  const [viewerFile, setViewerFile] = useState<{
    url: string;
    filename: string;
    fileExtension: string | null;
    uploadId: string;
    planId: string;
  } | null>(null);
  const { tokens } = useTheme<LensTheme>();
  const getUploadURL = useGetMitigationPlanUploadUrlMutation();
  const color = tokens.semantic.color;
  const sp = tokens.semantic.spacing.desktop.gap;

  const submittedOn = new Date(plan.createdAt).toLocaleDateString();
  const deadline = new Date(plan.deadline).toLocaleDateString();
  const reminderFrequencyLabel =
    plan.reminderFrequency === 1
      ? 'Daily'
      : plan.reminderFrequency === 7
        ? 'Weekly'
        : plan.reminderFrequency === 14
          ? 'Every 2 weeks'
          : plan.reminderFrequency === 30
            ? 'Monthly'
            : `every ${plan.reminderFrequency} days`;
  const reminderMaxCountLabel = plan.reminderMaxCount
    ? `up to ${plan.reminderMaxCount} reminder${plan.reminderMaxCount !== 1 ? 's' : ''}`
    : 'No limit';

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: false }}
      data-testid="mitigation-plan-details-drawer"
      sx={{
        '& .MuiDrawer-paper': {
          width: '567px',
          padding: sp[3].value,
          backgroundColor: color.surface.default.value,
          display: 'flex',
          flexDirection: 'column',
          gap: sp[2].value,
          overflowY: 'auto',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Typography
            component="h2"
            sx={{
              fontSize: '24px',
              fontWeight: tokens.semantic.fontWeight.emphasis.value,
              color: color.type.default.value,
            }}
          >
            {plan.title}
          </Typography>
          <Chip
            label={plan.status.charAt(0) + plan.status.slice(1).toLowerCase()}
            color={
              plan.status === 'SIGNED'
                ? 'success'
                : plan.status === 'OVERDUE'
                  ? 'error'
                  : 'warning'
            }
            size="small"
            sx={{ fontWeight: 600 }}
          />
        </Box>
        <IconButton
          onClick={onClose}
          aria-label="close"
          data-testid="mitigation-plan-details-drawer-close"
          sx={{
            color: color.action.secondary.onSecondary.value,
            '&:hover': {
              backgroundColor: color.action.secondary.hoverFill.value,
            },
          }}
        >
          <Close />
        </IconButton>
      </Box>

      {/* Tabs */}
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{
          borderBottom: `1px solid ${color.ui.divider.default.value}`,
          flexShrink: 0,
        }}
      >
        <Tab
          label="Plan details"
          id="plan-details-tab"
          aria-controls="plan-details-panel"
        />
        <Tab
          label="Additional approvals"
          id="additional-approvals-tab"
          aria-controls="additional-approvals-panel"
        />
      </Tabs>

      {/* Plan details tab */}
      {tab === 0 && (
        <Box
          role="tabpanel"
          id="plan-details-panel"
          aria-labelledby="plan-details-tab"
          sx={{ display: 'flex', flexDirection: 'column', gap: sp[2].value }}
        >
          {plan.description && (
            <Box
              sx={{
                backgroundColor: color.surface.variant.value,
                borderRadius: tokens.semantic.radius.md.value,
                padding: '16px',
              }}
            >
              <Typography
                variant="subtitle2"
                fontWeight={600}
                sx={{ mb: '8px' }}
              >
                Description
              </Typography>
              <Typography variant="body2">{plan.description}</Typography>
            </Box>
          )}

          <Box
            sx={{
              border: `1px solid ${color.ui.divider.default.value}`,
              borderRadius: tokens.semantic.radius.md.value,
              overflow: 'hidden',
            }}
          >
            {[
              { label: 'Submitted on', value: submittedOn },
              { label: 'Deadline', value: deadline },
              { label: 'Reminders frequency', value: reminderFrequencyLabel },
              {
                label: 'Reminders maximum count',
                value: reminderMaxCountLabel,
              },
            ].map(({ label, value }, i, arr) => (
              <Box
                key={label}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '12px 16px',
                  borderBottom:
                    i < arr.length - 1
                      ? `1px solid ${color.ui.divider.default.value}`
                      : 'none',
                }}
              >
                <Typography variant="body2" fontWeight={600}>
                  {label}
                </Typography>
                <Typography variant="body2" color={color.type.muted.value}>
                  {value}
                </Typography>
              </Box>
            ))}
          </Box>

          {plan.uploads.length > 0 && (
            <Box
              sx={{
                border: `1px solid ${color.ui.divider.default.value}`,
                borderRadius: tokens.semantic.radius.md.value,
                overflow: 'hidden',
              }}
            >
              {plan.uploads.map((u, i, arr) => (
                <Box
                  key={u.uploadId}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 16px',
                    borderBottom:
                      i < arr.length - 1
                        ? `1px solid ${color.ui.divider.default.value}`
                        : 'none',
                  }}
                >
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                  >
                    <InsertDriveFileOutlinedIcon
                      fontSize="small"
                      sx={{ color: color.type.muted.value }}
                    />
                    <Typography variant="body2">
                      {u.fileExtension
                        ? `Attachment ${u.uploadOrder + 1} (${u.fileExtension})`
                        : `Attachment ${u.uploadOrder + 1}`}
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ cursor: 'pointer' }}
                    onClick={async () => {
                      try {
                        const result = await getUploadURL.mutateAsync({
                          uploadId: u.uploadId,
                          planId: plan.id,
                        });
                        const ext = u.fileExtension
                          ? u.fileExtension.replace(/^\./, '')
                          : null;
                        setViewerFile({
                          url: result.getMitigationPlanUploadURL.url,
                          filename: ext
                            ? `Attachment ${u.uploadOrder + 1}.${ext}`
                            : `Attachment ${u.uploadOrder + 1}`,
                          fileExtension: u.fileExtension ?? null,
                          uploadId: u.uploadId,
                          planId: plan.id,
                        });
                      } catch (err) {
                        console.error(
                          'MitigationPlanDetailsDrawer: failed to get upload URL',
                          { uploadId: u.uploadId, planId: plan.id, err },
                        );
                      }
                    }}
                  >
                    View
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}

      {/* Additional approvals tab */}
      {tab === 1 && (
        <Box
          role="tabpanel"
          id="additional-approvals-panel"
          aria-labelledby="additional-approvals-tab"
        >
          <MitigationPlanApprovalChain plan={plan} onSigned={onPlanUpdated} />
        </Box>
      )}

      {viewerFile && (
        <MitigationPlanFileViewer
          open={Boolean(viewerFile)}
          filename={viewerFile.filename}
          fileExtension={viewerFile.fileExtension}
          url={viewerFile.url}
          uploadId={viewerFile.uploadId}
          planId={viewerFile.planId}
          onBack={() => setViewerFile(null)}
          onClose={() => {
            setViewerFile(null);
            onClose();
          }}
        />
      )}
    </Drawer>
  );
};
